if (typeof VariantDrawer !== 'function') {

  const MOBILE_MQ = window.matchMedia('(max-width: 727px)');

  class VariantDrawer extends HTMLElement {

    constructor() {
      super();
      this._syncing = false;
      this._boundKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productPage = document.getElementById(this.dataset.productPageId);
      if (!this.productPage) {
        return;
      }

      this.mainVariants = this.productPage.querySelector('.product-text product-variants');
      this.drawerVariants = this.querySelector('.variant-drawer__body product-variants');
      this.drawerFormHost = this.querySelector('product-form');
      this.drawerForm = this.drawerFormHost && this.drawerFormHost.querySelector('form');
      this.drawerMedia = this.querySelector('[data-js-drawer-media]');
      this.mainQty = this.productPage.querySelector('[data-js-product-quantity] input[name="quantity"]');
      this.drawerQty = this.querySelector('[data-js-product-quantity] input[name="quantity"]');
      this.mobileBar = document.querySelector(`product-mobile-purchase-bar[data-section-id="${this.sectionId}"]`);

      this.querySelectorAll('[data-js-drawer-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      if (this.mainVariants && this.drawerVariants) {
        this.mainVariants.addEventListener('VARIANT_CHANGE', () => {
          if (!this._syncing) {
            this._syncVariantInputs(this.mainVariants, this.drawerVariants);
          }
          this._updateDrawerUi();
        });
        this.drawerVariants.addEventListener('VARIANT_CHANGE', () => {
          if (!this._syncing) {
            this._syncVariantInputs(this.drawerVariants, this.mainVariants);
          }
          this._markMainVariantSelected();
          this._updateDrawerUi();
        });
        this.drawerVariants.addEventListener('change', () => {
          this._updateDrawerUi();
        });
        this.drawerVariants.addEventListener('input', () => {
          if (typeof this.drawerVariants.updateVariantOptionLabels === 'function') {
            this.drawerVariants.updateVariantOptionLabels();
          }
        });
      }

      this._bindQuantitySync();

      if (this.drawerFormHost) {
        this.drawerFormHost.addEventListener('add-to-cart', () => this.close());
      }

      this.productPage.addEventListener('click', (e) => {
        const openTrigger = e.target.closest('[data-js-open-variant-drawer], [data-js-product-add-to-cart].disabled');
        if (!openTrigger || !this._needsVariantSelection() || !MOBILE_MQ.matches) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.open();
      }, true);

      this.productPage.querySelector('product-form form')?.addEventListener('submit', (e) => {
        if (MOBILE_MQ.matches && this._needsVariantSelection()) {
          e.preventDefault();
          this.open();
        }
      }, true);

      VariantDrawer._instance = this;
      this._updateDrawerUi();
    }

    _bindQuantitySync() {
      if (!this.mainQty || !this.drawerQty) {
        return;
      }
      const sync = (from, to) => {
        if (to.value !== from.value) {
          to.value = from.value;
        }
      };
      this.mainQty.addEventListener('change', () => sync(this.mainQty, this.drawerQty));
      this.drawerQty.addEventListener('change', () => sync(this.drawerQty, this.mainQty));
    }

    _syncVariantInputs(from, to) {
      this._syncing = true;
      const fromContainers = from.querySelectorAll('[data-js-product-variant-container]');
      const toContainers = to.querySelectorAll('[data-js-product-variant-container]');
      fromContainers.forEach((fromContainer, index) => {
        const toContainer = toContainers[index];
        if (!toContainer) {
          return;
        }
        if (fromContainer.dataset.jsProductVariantContainer === 'radio') {
          const checked = fromContainer.querySelector('.product-variant__input:checked');
          toContainer.querySelectorAll('.product-variant__input:checked').forEach((input) => {
            input.checked = false;
          });
          if (checked) {
            const match = toContainer.querySelector(`.product-variant__input[value="${CSS.escape(checked.value)}"]`);
            if (match) {
              match.checked = true;
              match.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else if (typeof to.onVariantChange === 'function') {
            to.onVariantChange();
          }
        } else if (fromContainer.dataset.jsProductVariantContainer === 'select') {
          if (toContainer.value !== fromContainer.value) {
            toContainer.value = fromContainer.value;
            toContainer.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
      this._syncing = false;
      if ( typeof from._syncVariantCardSelectedUi === 'function' ) {
        from._syncVariantCardSelectedUi();
      }
      if ( typeof to._syncVariantCardSelectedUi === 'function' ) {
        to._syncVariantCardSelectedUi();
      }
    }

    _getActiveVariant() {
      if (this.drawerVariants?.currentVariant) {
        return this.drawerVariants.currentVariant;
      }
      if (this.mainVariants?.currentVariant) {
        return this.mainVariants.currentVariant;
      }
      return null;
    }

    _markMainVariantSelected() {
      if (
        !this.mainVariants ||
        !this.mainVariants.hasAttribute('data-variant-required')
      ) {
        return;
      }
      if (this.drawerVariants?.currentVariant || this.mainVariants.currentVariant) {
        this.mainVariants.classList.add('variant-selected');
        this.mainVariants.noVariantSelectedYet = false;
      }
    }

    _needsVariantSelection() {
      if (this._getActiveVariant()) {
        return false;
      }
      return (
        this.mainVariants &&
        this.mainVariants.hasAttribute('data-variant-required') &&
        !this.mainVariants.classList.contains('variant-selected')
      );
    }

    _refreshDomReferences() {
      if (this.dataset.productPageId) {
        this.productPage = document.getElementById(this.dataset.productPageId);
      }
      if (this.productPage) {
        this.mainVariants = this.productPage.querySelector('.product-text product-variants');
      }
      this.drawerVariants = this.querySelector('.variant-drawer__body product-variants');
      this.drawerFormHost = this.querySelector('product-form');
      this.drawerForm = this.drawerFormHost && this.drawerFormHost.querySelector('form');
      this.mobileBar = document.querySelector(`product-mobile-purchase-bar[data-section-id="${this.sectionId}"]`);
    }

    _updateDrawerUi() {
      const variant = this._getActiveVariant();
      const hasVariant = Boolean(variant);

      if (this.drawerQty) {
        const qtyWrap = this.drawerQty.closest('[data-js-product-quantity]');
        if (qtyWrap) {
          qtyWrap.style.display = 'flex';
        }
      }

      if (variant && this.drawerForm) {
        let idInput = this.drawerForm.querySelector('input[name="id"]');
        if (!idInput) {
          idInput = document.createElement('input');
          idInput.type = 'hidden';
          idInput.name = 'id';
          this.drawerForm.prepend(idInput);
        }
        idInput.value = variant.id;
      }

      if (hasVariant && this.drawerMedia) {
        const featured = variant.featured_image || (variant.featured_media && variant.featured_media.preview_image);
        const src = featured && (featured.src || featured.url);
        if (src) {
          let img = this.drawerMedia.querySelector('img');
          if (!img) {
            img = document.createElement('img');
            img.className = 'variant-drawer__image';
            this.drawerMedia.innerHTML = '';
            this.drawerMedia.appendChild(img);
          }
          if (img.src !== src) {
            img.src = src;
            img.alt = variant.title || '';
          }
        }
      }

      if (this.drawerVariants && typeof this.drawerVariants.updateVariantOptionLabels === 'function') {
        this.drawerVariants.updateVariantOptionLabels();
      }
      this.mobileBar?.updateState();
    }

  _syncMobileBarHeight() {
      if (this.mobileBar) {
        document.documentElement.style.setProperty(
          '--mobile-purchase-bar-height',
          `${this.mobileBar.offsetHeight}px`
        );
      }
    }

    open() {
      if (!MOBILE_MQ.matches) {
        return false;
      }
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('variant-drawer-open');
      this._syncMobileBarHeight();
      if (this.mainVariants && this.drawerVariants) {
        this._syncVariantInputs(this.mainVariants, this.drawerVariants);
        const checked = this.drawerVariants.querySelector('[data-js-product-variant-container="radio"] .product-variant__input:checked');
        if (checked) {
          checked.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this._updateDrawerUi();
      if (this.drawerQty && this.mainQty) {
        this.drawerQty.value = this.mainQty.value;
      }
      this.querySelector('.variant-drawer__close')?.focus();
      document.addEventListener('keydown', this._boundKeydown);
      return true;
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      this.hidden = true;
      document.body.classList.remove('variant-drawer-open');
      document.removeEventListener('keydown', this._boundKeydown);
      this.mobileBar?.updateState();
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
      }
    }

    static openIfNeeded() {
      if (VariantDrawer._instance && VariantDrawer._instance._needsVariantSelection()) {
        return VariantDrawer._instance.open();
      }
      return false;
    }

    static isMobile() {
      return MOBILE_MQ.matches;
    }
  }

  if (typeof customElements.get('variant-drawer') === 'undefined') {
    customElements.define('variant-drawer', VariantDrawer);
  }
}
